import React from 'react';
import { useSelector } from 'react-redux';

import { ProductCountContainer } from 'src/components/atoms/ProductCount/ProductCountContainer';
import type { TaxonomyHeaderBlockProps } from 'src/components/organisms/TaxonomyHeaderBlock/TaxonomyHeaderBlock';
import { TaxonomyHeaderBlock } from 'src/components/organisms/TaxonomyHeaderBlock/TaxonomyHeaderBlock';
import { useTaxonomyHeaderContent } from 'src/components/organisms/TaxonomyHeaderBlock/useTaxonomyHeaderContent';
import { selectIsShopSurfaceRoot } from 'src/redux/productsQuery/selectors/selectIsShopSurfaceRoot';
import { selectLowestActiveTaxonomyLabel } from 'src/redux/productsQuery/selectors/selectLowestActiveTaxonomyLabel';
import trackSEOCopyLinkClick from 'src/tracking/events/SEOCopyLinkClick';
import { trackSEOCopyToggle } from 'src/tracking/events/seoCopyToggle';
import { resolveTaxonomyHeading } from 'src/utils/resolveTaxonomyHeading';

/** Default h1 for the shop surface root (e.g. `/shop/all`), which has no product type value. */
export const SHOP_SURFACE_ROOT_HEADING = 'Shop all products';

export const TaxonomyHeaderBlockContainer = () => {
  const { description, heading, subheading } = useTaxonomyHeaderContent();
  const fallbackLabel = useSelector(selectLowestActiveTaxonomyLabel);
  const isSurfaceRoot = useSelector(selectIsShopSurfaceRoot);

  // The h1, HTML title and current-page breadcrumb all derive their text from
  // resolveTaxonomyHeading: the matched content heading when present, else the
  // lowest active taxonomy level sentence-cased at the point of display. At the
  // surface root there is neither, so the h1 falls back to a generic default.
  const resolvedHeading =
    resolveTaxonomyHeading({ heading, taxonomyLabel: fallbackLabel }) ??
    (isSurfaceRoot ? SHOP_SURFACE_ROOT_HEADING : undefined);

  if (!resolvedHeading) {
    return null;
  }

  const props: TaxonomyHeaderBlockProps = {
    description,
    heading: resolvedHeading,
    subheading,
    onAccordionToggle: ({ open }) => {
      trackSEOCopyToggle({ open, heading: resolvedHeading });
    },
    onDescriptionLinkClick: trackSEOCopyLinkClick,
    productCount: <ProductCountContainer />,
  };

  return <TaxonomyHeaderBlock {...props} />;
};
